import { useState, useEffect } from "react";
import { type StoreProduct } from "@/mocks/storeProducts";
import { useStoreProducts } from "@/hooks/useStoreProducts";
import ProductDetail from "./ProductDetail";

interface Props {
  onAddToCart: (product: StoreProduct, qty?: number) => void;
  onCartOpen: () => void;
  totalItems: number;
}

function formatKES(amount: number) {
  return `KES ${amount.toLocaleString()}`;
}

interface ProductCardProps {
  product: StoreProduct;
  onAdd: (p: StoreProduct) => void;
  onView: (p: StoreProduct) => void;
}

function ProductCard({ product, onAdd, onView }: ProductCardProps) {
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    onAdd(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <article className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-teal-200 transition-all duration-300 flex flex-col group">
      <div className="relative h-[200px] overflow-hidden cursor-pointer" onClick={() => onView(product)}>
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {product.badge && (
          <span className={`absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-md ${
            product.badge === "Sale" ? "bg-rose-500 text-white" :
            product.badge === "Best Seller" ? "bg-amber-500 text-white" :
            product.badge === "Best Value" ? "bg-teal-600 text-white" :
            "bg-gray-800 text-white"
          }`}>
            {product.badge}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-xs text-teal-600 font-semibold uppercase tracking-wider mb-1">{product.category}</span>
        <h3 className="text-gray-900 font-bold text-sm leading-snug mb-2 flex-1 cursor-pointer hover:text-teal-700" onClick={() => onView(product)}>{product.name}</h3>
        <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">{product.description}</p>
        <ul className="space-y-1 mb-4">
          {product.features.slice(0, 3).map((f) => (
            <li key={f} className="flex items-center gap-1.5 text-xs text-gray-600">
              <i className="ri-check-line text-teal-500 shrink-0"></i>
              {f}
            </li>
          ))}
        </ul>
        <div className="flex items-end justify-between mt-auto pt-3 border-t border-gray-100">
          <div>
            <p className="text-lg font-bold text-gray-900">{formatKES(product.price)}</p>
            {product.originalPrice && (
              <p className="text-xs text-gray-400 line-through">{formatKES(product.originalPrice)}</p>
            )}
            <p className="text-xs text-gray-400">{product.unit}</p>
          </div>
          <button
            onClick={handleAdd}
            disabled={!product.inStock}
            className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap ${
              added
                ? "bg-teal-100 text-teal-700"
                : product.inStock ? "bg-teal-600 hover:bg-teal-500 text-white" : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {!product.inStock ? (
              <>Out of stock</>
            ) : added ? (
              <><i className="ri-check-line"></i> Added</>
            ) : (
              <><i className="ri-add-line"></i> Add to Cart</>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function ProductGrid({ onAddToCart, onCartOpen, totalItems }: Props) {
  const { products: storeProducts, categories: storeCategories } = useStoreProducts();
  const [activeCategory, setActiveCategory] = useState("All Products");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState<StoreProduct | null>(null);

  // Deep link from Google Shopping / Facebook / Instagram: /store?product=ID opens that product
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked || !storeProducts.length) return;
    const id = Number(new URLSearchParams(window.location.search).get("product"));
    if (id) { const p = storeProducts.find((x) => Number(x.id) === id); if (p) { setViewing(p); setDeepLinked(true); } }
  }, [storeProducts, deepLinked]);

  const filtered = storeProducts.filter((p) => {
    const matchCat = activeCategory === "All Products" || p.category === activeCategory;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.category.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <section className="bg-gray-50 py-12 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-8">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 w-64 bg-white"
            />
          </div>
          <button
            onClick={onCartOpen}
            className="relative bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-shopping-basket-line"></i>
            My Cart
            {totalItems > 0 && (
              <span className="bg-white text-teal-700 text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {totalItems}
              </span>
            )}
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {storeCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-teal-600 text-white"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-teal-300"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-sm text-gray-500 mb-6">
          Showing <strong className="text-gray-900">{filtered.length}</strong> product{filtered.length !== 1 ? "s" : ""}
          {activeCategory !== "All Products" ? ` in "${activeCategory}"` : ""}
        </p>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((product) => (
              <ProductCard key={product.id} product={product} onAdd={onAddToCart} onView={setViewing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-gray-400">
            <i className="ri-store-2-line text-5xl mb-4 block"></i>
            <p>No products found. Try a different search or category.</p>
          </div>
        )}
      </div>
      {viewing && <ProductDetail product={viewing} onClose={() => setViewing(null)} onAdd={(p, q) => { onAddToCart(p, q); setViewing(null); }} />}
    </section>
  );
}
